/**
 * Once-per-session auto-reload for stale-chunk failures.
 *
 * Every deploy invalidates the hashed chunk URLs; a tab opened before the
 * deploy 404s on its next lazy navigation. Vite fires ``vite:preloadError``
 * and ``main.tsx`` reloads the tab once so it picks up the fresh
 * ``index.html``. The sessionStorage gate (which survives the reload)
 * prevents reload loops when the failure isn't staleness.
 *
 * The ``ErrorBoundary`` consults this module so the ~1s between "import
 * failed" and "reload lands" renders as a quiet "Updating…" state instead of
 * flashing the warning page — the warning is reserved for the give-up path
 * (the once-per-session reload already happened and the chunk still fails).
 */

/** sessionStorage key for the once-per-session reload gate (exported for tests). */
export const CHUNK_RELOAD_GATE_KEY = "hickory.chunk-reload";
const GATE_KEY = CHUNK_RELOAD_GATE_KEY;

// True from the moment THIS page lifetime fired its reload — the reload is in
// flight and any error UI is about to be replaced.
let reloadPending = false;

// Chromium / Firefox / Safari each word the failure differently; React lazy
// surfaces whichever the browser threw.
const CHUNK_ERROR_RE =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|chunkloaderror|unable to preload css/i;

export function isChunkLoadError(error: unknown): boolean {
  return (
    error instanceof Error &&
    CHUNK_ERROR_RE.test(`${error.name} ${error.message}`)
  );
}

/**
 * Reload the tab once per session gate. Returns true when a reload is now
 * (or already was) in flight; false when the gate says we already tried —
 * the give-up path, where the caller should surface a real error.
 */
export function requestChunkReload(): boolean {
  if (reloadPending) return true;
  if (sessionStorage.getItem(GATE_KEY)) return false;
  sessionStorage.setItem(GATE_KEY, "1");
  reloadPending = true;
  window.location.reload();
  return true;
}

/** sessionStorage key for the per-version reload gate (exported for tests). */
export const VERSION_RELOAD_GATE_KEY = "hickory.version-reload";

/**
 * Reload the tab onto a newly deployed build, at most once per version. The
 * gate stores the version and survives the reload, so a reload that lands on
 * the old build (a node mid-flip) cannot loop; a later deploy carries a new
 * version and earns its own reload. Returns true when a reload is in flight.
 */
export function requestVersionReload(version: string): boolean {
  if (reloadPending) return true;
  if (sessionStorage.getItem(VERSION_RELOAD_GATE_KEY) === version) return false;
  sessionStorage.setItem(VERSION_RELOAD_GATE_KEY, version);
  reloadPending = true;
  window.location.reload();
  return true;
}

/** sessionStorage key for the reload-buster gate (exported for tests). */
export const BUSTER_RELOAD_GATE_KEY = "hickory.buster-reload";
const BUSTER_MAX_RELOADS = 2;
const BUSTER_RETRY_GAP_MS = 10 * 60 * 1000;

interface BusterGate {
  buster: number;
  reloads: number;
  at: number;
}

function isBusterGate(value: unknown): value is BusterGate {
  if (typeof value !== "object" || value === null) return false;
  const { buster, reloads, at } = value as Record<string, unknown>;
  // Only counts this code can write pass: anything else fails closed.
  return (
    Number.isSafeInteger(buster) &&
    typeof reloads === "number" &&
    Number.isSafeInteger(reloads) &&
    reloads >= 1 &&
    reloads <= BUSTER_MAX_RELOADS &&
    Number.isFinite(at)
  );
}

/**
 * Reload the tab onto a hotfix that raised RELOAD_BUSTER (lib/reload-buster):
 * at most twice per buster value, at least ten minutes apart. The gate
 * survives the reload, so a reload that lands on the old build (a node
 * mid-flip, a stale cache) gets one spaced retry, never a loop. The gate only
 * ratchets up: once a higher value has been tried, a lower one is refused, so
 * two values served side by side cannot reset each other's count.
 *
 * Fails closed: when sessionStorage throws or holds anything unexpected, the
 * tab never force-reloads. Returns true when a reload is in flight.
 */
export function requestBusterReload(buster: number): boolean {
  if (reloadPending) return true;
  try {
    let reloads = 0;
    const raw = sessionStorage.getItem(BUSTER_RELOAD_GATE_KEY);
    if (raw !== null) {
      const gate: unknown = JSON.parse(raw);
      if (!isBusterGate(gate) || gate.buster > buster) return false;
      if (gate.buster === buster) {
        if (gate.reloads >= BUSTER_MAX_RELOADS) return false;
        if (Date.now() - gate.at < BUSTER_RETRY_GAP_MS) return false;
        reloads = gate.reloads;
      }
    }
    const next: BusterGate = { buster, reloads: reloads + 1, at: Date.now() };
    sessionStorage.setItem(BUSTER_RELOAD_GATE_KEY, JSON.stringify(next));
  } catch {
    return false;
  }
  reloadPending = true;
  window.location.reload();
  return true;
}

/**
 * Whether a chunk failure right now would self-heal: either a reload is
 * already in flight, or the gate is open so ``requestChunkReload`` will fire
 * one. The ErrorBoundary renders the quiet "Updating…" state exactly when
 * this is true.
 *
 * The open-gate clause covers a React lifecycle ordering subtlety: the
 * boundary RENDERS (after ``getDerivedStateFromError``) before its
 * ``componentDidCatch`` runs ``requestChunkReload`` in the commit phase. On
 * that first paint ``reloadPending`` may still be false even though a reload
 * is about to fire — without the gate check the warning page would flash for
 * one frame, which is the exact jank this module exists to remove.
 */
export function isChunkReloadImminent(): boolean {
  return reloadPending || !sessionStorage.getItem(GATE_KEY);
}

/**
 * Clear the gate once the app has been alive long enough that any further
 * preload failure is a genuinely new event, not a loop of the same one.
 */
export function armChunkReloadGateClear(delayMs = 5000): void {
  setTimeout(() => sessionStorage.removeItem(GATE_KEY), delayMs);
}

/**
 * The ``vite:preloadError`` listener (installed by ``main.tsx``).
 *
 * NEVER ``event.preventDefault()`` here. Vite's preload helper only
 * re-throws the import failure when the event is NOT default-prevented;
 * preventing it makes the dynamic import RESOLVE WITH ``undefined``, so
 * React.lazy throws a TypeError that dodges ``isChunkLoadError`` and the
 * ErrorBoundary renders the generic "unexpected data / Try Again" page —
 * with a Try Again that can never work, because React.lazy memoizes the
 * failed result. Letting the rejection propagate with its chunk identity
 * intact is what routes it to the boundary's quiet "Updating…" state (or
 * the give-up UI with a working hard-Reload). Verified in real Chromium.
 */
export function onVitePreloadError(event: Event): void {
  if (!requestChunkReload()) {
    console.error("[chunk-reload] preload failed after reload, giving up", event);
  }
}
